import { ChevronLeft } from 'lucide-react'
import { useMemo, useState } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { TextButton, TextLink } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import {
  formatMoneyValue,
  formatMonthName,
  getFirstName,
  getNextMonthName,
  getPriorMonthEnrollmentCutoffDate,
  getPriorMonthName,
} from 'utils/billing'
import {
  useGetEmployersMembersIdMonthlyAllowance,
} from 'utils/web_api'
import { type EmployersMemberInvoiceDetail } from 'utils/web_api.schemas'

import { CostBreakdownItem } from './CostBreakdownItem'
import { useInvoice } from './InvoiceContext'
import { WhyBilledMoreThanOneMonthModal } from './modals/WhyBilledMoreThanOneMonthModal'

interface MemberInvoiceDetailsProps {
  memberInvoice: EmployersMemberInvoiceDetail | null
  onBack: () => void
}

export function MemberInvoiceDetails ({ memberInvoice, onBack }: MemberInvoiceDetailsProps) {
  const { invoice } = useInvoice()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const coverageDate = useMemo(() => {
    if (!invoice?.period_end_date) return null
    const endDate = new Date(invoice.period_end_date)
    return new Date(endDate.getFullYear(), endDate.getMonth(), 1).toISOString().split('T')[0]
  }, [invoice?.period_end_date])

  const planYear = useMemo(() => {
    return memberInvoice?.plan_year ?? (invoice?.period_end_date ? new Date(invoice.period_end_date).getFullYear() : null)
  }, [memberInvoice?.plan_year, invoice?.period_end_date])

  const {
    data: monthlyAllowanceData,
    isLoading: isLoadingAllowance,
  } = useGetEmployersMembersIdMonthlyAllowance(
    memberInvoice?.member_id || '',
    {
      coverage_date: coverageDate ?? '',
      plan_year: planYear ?? undefined,
    },
    {
      query: {
        enabled: !!memberInvoice && !!memberInvoice.member_id && !!coverageDate,
        retry: false,
      },
    }
  )

  if (!memberInvoice) {
    return (
      <div className='p-6'>
        <div className='text-gray-500'>Member invoice not found</div>
      </div>
    )
  }

  const monthName = formatMonthName(invoice.period_end_date)
  const periodEndDate = new Date(invoice.period_end_date)
  const nextMonthYearFormatted = `${getNextMonthName(invoice.period_end_date)} ${new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1).getFullYear()}`
  const nextMonthFirstDay = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1)
  const nextMonthLastDay = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 2, 0)
  const nextMonthFirstDayFormatted = nextMonthFirstDay.toLocaleString('en-US', { month: 'long', day: 'numeric' })
  const nextMonthLastDayFormatted = nextMonthLastDay.toLocaleString('en-US', { month: 'long', day: 'numeric' })

  const { amounts } = memberInvoice
  const prefundingAmount = amounts?.total_insurance_premiums
  const qmeTotal = amounts?.other
  const ineligibleSpend = memberInvoice.posttax_employee_qme_overage
  const qualifiedExpenses = memberInvoice.pretax_employer_qme_reimbursement
  const doubleBillingTotal = amounts?.double_billing_total
  const hasDoubleBilling = doubleBillingTotal && convertGrapeMoneyToNumber(doubleBillingTotal) > 0
  const coverageCorrections = amounts?.coverage_corrections_excluding_double_billing ||
    amounts?.coverage_corrections
  const totalAmount = amounts?.total

  const firstName = getFirstName(memberInvoice.full_name)
  const monthlyAllowance = monthlyAllowanceData?.monthly_allowance
  const expectedReturn = convertGrapeMoneyToNumber(totalAmount) - convertGrapeMoneyToNumber(monthlyAllowance)

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <TextButton
          color='gray'
          leftIcon={ChevronLeft}
          onClick={onBack}
        >
          Back to {monthName} healthcare costs
        </TextButton>
      </div>

      <h2 className='heading-md mb-4'>{memberInvoice.full_name || '---'}</h2>

      <div className='flex items-center justify-between mb-6'>
        <span className='body-sm text-subdued'>Member cost breakdown</span>
        <div className='bg-white rounded-[40px] py-2 px-3 border' style={{ borderColor: 'var(--color-stroke-light, #E5E5E5)' }}>
          <span className='body text-gray-800'>
            Monthly allowance:{' '}
            {isLoadingAllowance
              ? (
                <span className='inline-flex items-center'>
                  <LoadingSpinnerCentered height='16px' width='16px' />
                </span>
                )
              : formatMoneyValue(monthlyAllowance)}
          </span>
        </div>
      </div>

      <div className='space-y-6'>
        <div>
          <CostBreakdownItem
            label={`Prepayment for ${nextMonthYearFormatted} insurance premiums`}
            value={formatMoneyValue(prefundingAmount)}
            description={`Billing in advance for insurance coverage from ${nextMonthFirstDayFormatted} – ${nextMonthLastDayFormatted}.`}
          />
          <div className='mt-2 space-y-1'>
            {memberInvoice.medical_premiums_total && convertGrapeMoneyToNumber(memberInvoice.medical_premiums_total) > 0 && (
              <CostBreakdownItem
                label='Upcoming Medical Premium'
                value={formatMoneyValue(memberInvoice.medical_premiums_total)}
                indented
                variant='subdued'
              />
            )}
            {memberInvoice.dental_premiums_total && convertGrapeMoneyToNumber(memberInvoice.dental_premiums_total) > 0 && (
              <CostBreakdownItem
                label='Upcoming Dental Premium'
                value={formatMoneyValue(memberInvoice.dental_premiums_total)}
                indented
                variant='subdued'
              />
            )}
            {memberInvoice.vision_premiums_total && convertGrapeMoneyToNumber(memberInvoice.vision_premiums_total) > 0 && (
              <CostBreakdownItem
                label='Upcoming Vision Premium'
                value={formatMoneyValue(memberInvoice.vision_premiums_total)}
                indented
                variant='subdued'
              />
            )}
          </div>
        </div>

        {hasDoubleBilling && (
          <div>
            <CostBreakdownItem
              label={`Payment for ${monthName} insurance premiums`}
              value={formatMoneyValue(doubleBillingTotal)}
              description={`Insurance coverage for new employees who enrolled for ${monthName} coverage after ${getPriorMonthEnrollmentCutoffDate(invoice.period_end_date)}.`}
            />
            <div className='mt-2'>
              <TextLink
                color='purple'
                href='#'
                onClick={(e) => {
                  e.preventDefault()
                  setIsModalOpen(true)
                }}
              >
                Why was I billed for 2 months of coverage?
              </TextLink>
            </div>
          </div>
        )}

        <div>
          <CostBreakdownItem
            label={`Changes in ${getPriorMonthName(invoice.period_end_date)} billing`}
            value={formatMoneyValue(coverageCorrections)}
            description={`Corrections for ${monthName} healthcare costs since the prior billing period.`}
          />
        </div>

        <div>
          <CostBreakdownItem
            label='Qualified medical expenses'
            value={formatMoneyValue(qmeTotal)}
            description='Money your team used from their leftover allowance to cover copays, prescriptions, etc.'
          />
          <div className='mt-2 space-y-1'>
            <CostBreakdownItem
              label='Ineligible spend'
              value={formatMoneyValue(ineligibleSpend)}
              indented
              variant='subdued'
            />
            <CostBreakdownItem
              label='Qualified medical expenses'
              value={formatMoneyValue(qualifiedExpenses)}
              indented
              variant='subdued'
            />
          </div>
        </div>

        <hr className='my-6 border-t border-stroke-[#919191]' />

        <div>
          <CostBreakdownItem
            label='Total healthcare costs'
            value={formatMoneyValue(totalAmount)}
            description={`The total amount billed for ${firstName}`}
            variant='bold'
          />
        </div>
        <div>
          <CostBreakdownItem
            label='Monthly allowance'
            value={`-${formatMoneyValue(monthlyAllowance)}`}
            description={`${firstName}'s monthly allowance`}
            valueClassName='body-bold text-green-400'
          />
        </div>
        {
          monthlyAllowance && totalAmount && convertGrapeMoneyToNumber(monthlyAllowance) < convertGrapeMoneyToNumber(totalAmount) && (
            <>
              <hr className='my-6 border-t border-stroke-[#919191]' />
              <div>
                <CostBreakdownItem
                  label='Expected return'
                  value={`${formatMoney(expectedReturn)}`}
                  description={`The amount you'll recoup from ${firstName} through payroll deductions`}
                />
              </div>
            </>
          )
        }
      </div>
      <WhyBilledMoreThanOneMonthModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  )
}
